import type { TenantStatus } from '@inou/shared';
import { useState } from 'react';

import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input, NumberInput } from '@/components/ui/input';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import { Toggle } from '@/components/ui/toggle';
import { ApiError, api, type HqTenant } from '@/lib/api';
import { useApi } from '@/lib/useApi';

import { FeatureFlagEditor } from './FeatureFlagEditor';

/**
 * テナント一覧と編集。
 *
 * 運営が触れるのは「状態」「手動付与シート」「機能フラグ」だけ。
 * purchased_*_seats は Stripe が正なので、ここでは編集させない (読み取り専用で表示する)。
 */
export function TenantsPage() {
  const { data, error, loading, reload } = useApi<{ tenants: HqTenant[] }>('/hq/tenants');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<OpenedTenant | null>(null);
  const [query, setQuery] = useState('');

  // 運営はテナント数が増えるほど探しにくくなるので、絞り込みを置く
  const tenants = (data?.tenants ?? []).filter((tenant) => matchesQuery(query, tenant.name));

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex items-center justify-between gap-4">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="組織名で絞り込む"
          className="w-80"
        />
        <Button variant="primary" onClick={() => setCreating((value) => !value)}>
          {creating ? '閉じる' : 'テナントを開通する'}
        </Button>
      </div>

      {creating ? (
        <CreateTenantForm
          onCreated={(result) => {
            setOpened(result);
            setCreating(false);
            reload();
          }}
        />
      ) : null}

      {opened ? <OpenedTenantNotice opened={opened} onDismiss={() => setOpened(null)} /> : null}

      <StateBlock
        loading={loading}
        error={error}
        empty={data?.tenants.length === 0}
        emptyMessage="テナントがありません。"
      >
        <div className="border border-line bg-surface-raised">
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>TENANT</Th>
                  <Th>STATUS</Th>
                  <Th className="text-right">FULL (利用 / 枠)</Th>
                  <Th className="text-right">MCP (利用 / 枠)</Th>
                  <Th>FLAGS</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <TenantRow
                    key={tenant.id}
                    tenant={tenant}
                    expanded={editing === tenant.id}
                    onToggle={() => setEditing(editing === tenant.id ? null : tenant.id)}
                    onSaved={reload}
                    onReissued={(result) =>
                      setOpened({
                        tenant: { name: tenant.name },
                        admin: result.admin,
                        invitation: result.invitation,
                      })
                    }
                  />
                ))}
              </tbody>
            </Table>
          </TableWrap>
          {tenants.length === 0 ? (
            <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
              「{query}」に一致するテナントはありません。
            </p>
          ) : null}
        </div>
      </StateBlock>
    </div>
  );
}

/**
 * 状態の表示。
 *
 * 停止 (SUSPENDED) はログインごと塞ぐ、解約 (CANCELED) と削除予定
 * (PENDING DELETION) は閲覧とエクスポートだけ通る。性質が違うので
 * バッジも分ける。
 */
const STATUS_LABELS: Record<TenantStatus, { label: string; alert: boolean }> = {
  active: { label: 'ACTIVE', alert: false },
  suspended: { label: 'SUSPENDED', alert: true },
  canceled: { label: 'CANCELED', alert: false },
  pending_deletion: { label: 'PENDING DELETION', alert: true },
};

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('ja-JP');
}

/** 削除予定日の既定値 (30 日後) を 'YYYY-MM-DD' で返す */
function defaultDeletionDate(): string {
  const date = new Date(Date.now() + 30 * 86400 * 1000);
  return date.toISOString().slice(0, 10);
}

interface OpenedTenant {
  tenant: { name: string };
  admin: { email: string };
  invitation: { url: string; expiresAt: number };
}

/**
 * テナントの開通。
 *
 * テナントと最初の管理者は 2 つで 1 つの操作。管理者が居ないテナントを
 * 作っても誰も入れないので、フォームを分けない。
 */
function CreateTenantForm({ onCreated }: { onCreated: (result: OpenedTenant) => void }) {
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [grantedFullSeats, setGrantedFullSeats] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await api.post<OpenedTenant>('/hq/tenants', {
          name: name.trim(),
          adminEmail: adminEmail.trim(),
          adminName: adminName.trim(),
          grantedFullSeats,
        }),
      );
      setName('');
      setAdminEmail('');
      setAdminName('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const ready = name.trim() !== '' && adminEmail.trim() !== '' && adminName.trim() !== '';

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">テナントを開通する</h2>
      <FieldRow>
        <Field label="組織名" className="min-w-48">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="株式会社アクメ"
          />
        </Field>
        <Field label="初期管理者のメール" className="min-w-56">
          <Input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="owner@acme.example"
          />
        </Field>
        <Field label="初期管理者の表示名" className="min-w-40">
          <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </Field>
        <Field label="付与シート (Full)" className="w-32" hint="MCP 枠は開通後に付与">
          <NumberInput
            min={1}
            value={grantedFullSeats}
            onChange={(e) => setGrantedFullSeats(Number(e.target.value))}
          />
        </Field>
        <Button variant="primary" onClick={submit} disabled={busy || !ready}>
          開通
        </Button>
      </FieldRow>
      <p className="text-xs leading-relaxed text-ink-subtle">
        購入済みシート (purchased_seats) は Stripe が正なので、ここでは付与シートだけを渡します。
      </p>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}

/** 開通直後にしか出せない招待リンク */
function OpenedTenantNotice({
  opened,
  onDismiss,
}: {
  opened: OpenedTenant;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex flex-col gap-3 border border-brand-700 bg-surface-raised px-6 py-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold">{opened.tenant.name} の初期管理者の招待リンク</h2>
        <Button variant="ghost" onClick={onDismiss}>
          閉じる
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-ink-subtle">
        {opened.admin.email} 宛の招待リンクです。いま一度だけ表示されます。 有効期限は{' '}
        {new Date(opened.invitation.expiresAt * 1000).toLocaleString('ja-JP')} です。
      </p>
      <div className="flex gap-2">
        <Input readOnly value={opened.invitation.url} className="flex-1 font-mono text-xs" />
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(opened.invitation.url);
            setCopied(true);
          }}
        >
          {copied ? 'コピーしました' : 'コピー'}
        </Button>
      </div>
    </div>
  );
}

function TenantRow({
  tenant,
  expanded,
  onToggle,
  onSaved,
  onReissued,
}: {
  tenant: HqTenant;
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onReissued: (result: {
    admin: { email: string };
    invitation: { url: string; expiresAt: number };
  }) => void;
}) {
  const [deletionDate, setDeletionDate] = useState(
    tenant.deletionScheduledAt === null
      ? defaultDeletionDate()
      : new Date(tenant.deletionScheduledAt * 1000).toISOString().slice(0, 10),
  );
  const [grantedFullSeats, setGrantedFullSeats] = useState(tenant.seats.full.grantedSeats);
  const [grantedMcpSeats, setGrantedMcpSeats] = useState(tenant.seats.mcp.grantedSeats);
  const [flags, setFlags] = useState(tenant.featureFlags);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 押し間違いが重いので 2 段階にする。ブラウザの confirm() は使わず、
  // 画面内で確認 UI を出す。
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeTokens, setRevokeTokens] = useState(false);

  const flagCount = Object.values(tenant.featureFlags).filter(Boolean).length;
  const overSeats = tenant.seats.full.activeUsers > tenant.seats.full.availableSeats;
  const overMcpSeats = tenant.seats.mcp.activeUsers > tenant.seats.mcp.availableSeats;

  async function patch(body: Record<string, unknown>, note: string) {
    setBusy(true);
    setMessage(null);
    try {
      await api.patch(`/hq/tenants/${tenant.id}`, body);
      setMessage(note);
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function revokeSessions() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<{ sessions: number; tokens: number }>(
        `/hq/tenants/${tenant.id}/revoke-sessions`,
        { includeTokens: revokeTokens },
      );
      setMessage(
        `セッションを ${result.sessions} 件失効させました` +
          (revokeTokens ? `（MCP のトークン ${result.tokens} 件も失効）` : ''),
      );
      setConfirmingRevoke(false);
      setRevokeTokens(false);
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <Td>
          <span className="font-medium">{tenant.name}</span>
        </Td>
        <Td>
          <div className="flex flex-col items-start gap-1">
            <Badge variant={STATUS_LABELS[tenant.status].alert ? 'alert' : 'outline'}>
              {STATUS_LABELS[tenant.status].label}
            </Badge>
            {tenant.deletionScheduledAt !== null ? (
              <span className="font-mono text-[10px] text-ink-subtle">
                {formatDate(tenant.deletionScheduledAt)}
              </span>
            ) : null}
            {tenant.pendingAdminEmail ? <Badge>未開通</Badge> : null}
          </div>
        </Td>
        <TdNum className={overSeats ? 'text-alert' : undefined}>
          {tenant.seats.full.activeUsers} / {tenant.seats.full.availableSeats}
        </TdNum>
        <TdNum className={overMcpSeats ? 'text-alert' : undefined}>
          {tenant.seats.mcp.activeUsers} / {tenant.seats.mcp.availableSeats}
        </TdNum>
        <Td className="font-mono text-xs text-ink-subtle">
          {flagCount > 0 ? `${flagCount} 有効` : '—'}
        </Td>
        <Td className="text-right">
          <Button onClick={onToggle} aria-expanded={expanded}>
            {expanded ? '閉じる' : '編集'}
          </Button>
        </Td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={7} className="border-t border-line bg-surface px-4 py-5">
            <div className="grid gap-8 md:grid-cols-3">
              <section className="flex flex-col gap-3">
                <h3 className="text-sm font-bold">アカウント</h3>
                <p className="text-xs leading-relaxed text-ink-muted">
                  {'強制停止するとログインごと塞がり、このテナントの API とダッシュボードが' +
                    'すべて止まります。解約と削除予定は閲覧とデータのエクスポートだけを通すので、' +
                    '契約が終わったテナントにはこちらを使ってください。'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tenant.status === 'active' ? (
                    <Button
                      variant="alert"
                      disabled={busy}
                      onClick={() => patch({ status: 'suspended' }, '停止しました')}
                    >
                      強制停止する
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={busy}
                      onClick={() => patch({ status: 'active' }, '通常の状態に戻しました')}
                    >
                      通常の状態に戻す
                    </Button>
                  )}
                  {tenant.status === 'canceled' ? null : (
                    <Button
                      disabled={busy}
                      onClick={() => patch({ status: 'canceled' }, '解約済みにしました')}
                    >
                      解約済みにする
                    </Button>
                  )}
                </div>

                <FieldRow className="gap-x-2">
                  <Field
                    label="削除予定日"
                    className="w-40"
                    hint="この日以降に消す想定。削除の実行はまだありません。"
                  >
                    <Input
                      type="date"
                      value={deletionDate}
                      onChange={(e) => setDeletionDate(e.target.value)}
                      className="font-mono"
                    />
                  </Field>
                  <Button
                    variant="alert"
                    disabled={busy || deletionDate === ''}
                    onClick={() =>
                      patch(
                        {
                          status: 'pending_deletion',
                          deletionScheduledAt: Math.floor(
                            new Date(`${deletionDate}T00:00:00`).getTime() / 1000,
                          ),
                        },
                        '削除予定にしました',
                      )
                    }
                  >
                    削除予定にする
                  </Button>
                </FieldRow>

                <p className="text-xs leading-relaxed text-ink-muted">
                  {'インシデント時に、このテナントのログイン中の端末をすべて切ります。'}
                  {'テナント自体は止まらないので、切られた人はログインし直せます。'}
                </p>
                {confirmingRevoke ? (
                  <div className="flex flex-col gap-3 border border-alert bg-surface px-4 py-3">
                    <p className="text-xs leading-relaxed text-ink">
                      {`${tenant.name} のログイン中の端末をすべて切ります。この操作は戻せません。`}
                    </p>
                    <div className="flex items-center gap-2">
                      <Toggle
                        checked={revokeTokens}
                        onChange={setRevokeTokens}
                        label="MCP のトークンも失効させる"
                        disabled={busy}
                      />
                      <span className="text-xs text-ink-muted">
                        {'MCP のトークンも失効させる（このテナントの MCP が全員分止まります）'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="alert" disabled={busy} onClick={revokeSessions}>
                        実行する
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          setConfirmingRevoke(false);
                          setRevokeTokens(false);
                        }}
                      >
                        やめる
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Button disabled={busy} onClick={() => setConfirmingRevoke(true)}>
                      全セッションを失効させる
                    </Button>
                  </div>
                )}

                {tenant.pendingAdminEmail ? (
                  <>
                    <p className="text-xs leading-relaxed text-ink-muted">
                      初期管理者 ({tenant.pendingAdminEmail}) がまだ招待を受諾していません。
                      期限が切れるとこのテナントには誰も入れなくなるので、ここから再発行できます。
                    </p>
                    <div>
                      <Button
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          setMessage(null);
                          try {
                            onReissued(
                              await api.post<{
                                admin: { email: string };
                                invitation: { url: string; expiresAt: number };
                              }>(`/hq/tenants/${tenant.id}/admin-invitation`),
                            );
                            onSaved();
                          } catch (cause) {
                            setMessage(
                              cause instanceof ApiError ? cause.message : '通信に失敗しました',
                            );
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        初期管理者の招待を再発行
                      </Button>
                    </div>
                  </>
                ) : null}
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="text-sm font-bold">手動付与シート</h3>
                <p className="text-xs leading-relaxed text-ink-muted">
                  Stripe の購入枠（Full {tenant.seats.full.purchasedSeats} / MCP{' '}
                  {tenant.seats.mcp.purchasedSeats}）とは別勘定です。 webhook
                  で購入枠が更新されても、ここで付けた枠は消えません。
                </p>
                <FieldRow className="gap-x-2">
                  <Field label="Full Management" className="w-32">
                    <NumberInput
                      min={0}
                      step={1}
                      value={grantedFullSeats}
                      onChange={(e) => setGrantedFullSeats(Number(e.target.value))}
                      aria-label="手動付与シート (Full Management)"
                    />
                  </Field>
                  <Field label="MCP Connect" className="w-32">
                    <NumberInput
                      min={0}
                      step={1}
                      value={grantedMcpSeats}
                      onChange={(e) => setGrantedMcpSeats(Number(e.target.value))}
                      aria-label="手動付与シート (MCP Connect)"
                    />
                  </Field>
                  <Button
                    variant="primary"
                    disabled={
                      busy ||
                      (grantedFullSeats === tenant.seats.full.grantedSeats &&
                        grantedMcpSeats === tenant.seats.mcp.grantedSeats)
                    }
                    onClick={() =>
                      patch({ grantedFullSeats, grantedMcpSeats }, 'シートを更新しました')
                    }
                  >
                    保存
                  </Button>
                </FieldRow>
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="text-sm font-bold">機能フラグ</h3>
                <FeatureFlagEditor flags={flags} onChange={setFlags} disabled={busy} />
                <div>
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => patch({ featureFlags: flags }, 'フラグを更新しました')}
                  >
                    保存
                  </Button>
                </div>
              </section>
            </div>

            {message ? <p className="pt-4 text-xs text-ink-muted">{message}</p> : null}

            <dl className="flex flex-wrap gap-x-8 gap-y-1 pt-5 font-mono text-[11px] text-ink-subtle">
              <div className="flex gap-2">
                <dt>TENANT ID</dt>
                <dd className="text-ink-secondary">{tenant.id}</dd>
              </div>
              <div className="flex gap-2">
                <dt>STRIPE CUSTOMER</dt>
                <dd className="text-ink-secondary">{tenant.stripeCustomerId ?? '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt>SUBSCRIPTION</dt>
                <dd className="text-ink-secondary">{tenant.stripeSubscriptionId ?? '—'}</dd>
              </div>
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}
